import React, { useRef, useState } from "react";
import styled from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import {
    ACTIVATE_MASK_ITEM,
    SET_ACTIVE_LAYOUT_GROUP,
    SET_FAVORITE_OVERLAY_ACTIVE,
    SET_LAYOUT_OVERLAY_ACTIVE,
} from "../../store/actions/configurationActions";
import { SET_SIDE } from "../../store/actions/sideMenuActions";
import { MASK_LOADED } from "../../store/actions/imageActions";
import { getMenuPosition, buildSelectionHighlightData, getMaskValueForMouseEvent } from "../../utilities/maskUtils";
import Constants from "../../constants";
import {
    getFrontendSettings,
    getImageAndMask,
    getMaskData,
    getMaskMetadata,
    getSideMenuSide,
} from "../../store/sagas/selectors";
import GaReporter from "../../utilities/gaReporter";
import { isMenuCategory } from "../../utilities/itemUtilities";
import { ConfigImageContainer } from "../Image/ConfigImageContainer";
import { ConfigImage } from "../Image/ConfigImage";
import { SelectionCanvas } from "../Image/SelectionCanvas";
import { SelectionHighlightData } from "../../models/selectionHightlightData";

const MaskImage = styled.img`
    position: fixed;
    flex: 1 1 auto;
    opacity: 0;
    top: 0;
    left: 0;
    z-index: -3;
`;

export const ConfiguratorImages = ({
    fullscreen,
    setShowFilter,
}: {
    fullscreen?: boolean;
    setShowFilter: Function;
}) => {
    const dispatch = useDispatch();

    const [isItemSelectable, setIsItemSelectable] = useState(false);
    const [highlightData, setHighlightData] = useState<SelectionHighlightData>();

    const imageAndMask = useSelector(getImageAndMask);
    const maskMetadata = useSelector(getMaskMetadata);
    const maskData = useSelector(getMaskData);
    const frontendSettings = useSelector(getFrontendSettings);
    const menuSide = useSelector(getSideMenuSide);

    const configImageRef = useRef<HTMLImageElement>(null);
    const maskRef = useRef<HTMLImageElement>(null);

    const handleMaskLoad = () => {
        dispatch({ type: MASK_LOADED, maskElement: maskRef.current });
        requestAnimationFrame(() => {
            dispatch({ type: SET_LAYOUT_OVERLAY_ACTIVE, layoutOverlayActive: false });
        });
    };

    const onImageLoad = () => {
        requestAnimationFrame(() => {
            dispatch({ type: SET_FAVORITE_OVERLAY_ACTIVE, favoriteOverlayActive: false });
        });
    };

    const getSelectedMaskItemData = (maskValue: number | null) => {
        return maskValue ? _.get(maskMetadata, maskValue) : null;
    };

    const handleImageMouseMove = (event: any) => {
        const maskValue = getMaskValueForMouseEvent(event, maskRef.current, maskData);
        const currentSelectedMaskItem = getSelectedMaskItemData(maskValue);
        return !!currentSelectedMaskItem?.MENU_PATH;
    };

    const triggerSelectionBlink = (maskValue: number) => {
        const selectedValues = [maskValue];
        const newHighlightData = buildSelectionHighlightData(
            maskData,
            maskRef.current,
            configImageRef.current,
            selectedValues
        );
        setHighlightData(newHighlightData);
    };

    const onImageClick = (event: any) => {
        const maskValue = getMaskValueForMouseEvent(event, maskRef.current, maskData);
        if (!maskValue) return;
        const currentSelectedMaskItem = getSelectedMaskItemData(maskValue);
        if (!currentSelectedMaskItem) return;

        const itemId = currentSelectedMaskItem[Constants.itemMetadataKey];
        let menuPath = currentSelectedMaskItem[Constants.menuPathMetadataKey];
        if (!menuPath) return;

        triggerSelectionBlink(maskValue);
        const sideMenuPosition = getMenuPosition(
            maskData,
            maskValue,
            maskRef.current?.naturalHeight,
            maskRef.current?.naturalWidth,
            menuSide
        );

        // If "mask_click_opens_base_menu" is specified,
        // we should open the main menu instead of the deepest menu.
        // Remove everything from the first slash to only get the main menu.
        const parts = menuPath.split("/");

        // Remove the last part which is the clicked item itself.
        parts.pop();
        if (parts.length === 0) throw new Error("Invalid menu path");

        const mainMenu = parts[0];
        if (frontendSettings?.mask_click_opens_base_menu) {
            menuPath = parts.length > 1 && isMenuCategory(parts[1]) ? parts.slice(0, 2).join("/") : mainMenu;
        } else {
            menuPath = parts.join("/");
        }
        const modifiedMaskItem = { ...currentSelectedMaskItem };
        modifiedMaskItem[Constants.menuPathMetadataKey] = menuPath;

        GaReporter.event("image_click", "Configurator", mainMenu);

        setShowFilter(false);
        dispatch({ type: ACTIVATE_MASK_ITEM, itemId, menuPath });
        dispatch({ type: SET_SIDE, side: sideMenuPosition });

        const layoutGroup: string = currentSelectedMaskItem[Constants.layoutGroupMetadataKey];
        if (layoutGroup) {
            dispatch({ type: SET_ACTIVE_LAYOUT_GROUP, activeLayoutGroup: layoutGroup });
        }
    };

    const imageWidth = configImageRef.current?.naturalWidth;
    const imageHeight = configImageRef.current?.naturalHeight;
    return (
        <ConfigImageContainer
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            fullscreen={fullscreen}
            withDesktopGradientOverlay
        >
            <ConfigImage
                onMouseMove={e => setIsItemSelectable(handleImageMouseMove(e))}
                isSelectableItem={isItemSelectable}
                ref={configImageRef}
                onClick={onImageClick}
                onLoad={onImageLoad}
                fullscreen={fullscreen || false}
                alt=""
                src={imageAndMask?.configurationImagePath || ""}
            />
            <MaskImage
                onLoad={handleMaskLoad}
                crossOrigin="anonymous"
                ref={maskRef}
                alt=""
                src={imageAndMask?.maskPath || ""}
            />
            <SelectionCanvas highlightData={highlightData} />
        </ConfigImageContainer>
    );
};
